/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { IconLocationBroken } from '@tabler/icons-react';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';

import { Button, ButtonLink } from '~/domains/design-system/Button';
import type { CheckRequestError } from '~/utils/geocomply';

import StatusModalBody from './components/StatusModalBody';
import { useMantineTheme } from '@mantine/core';

function InvalidLocationModal({
  isOpen,
  onClose,
  onRecheckLocation,
  locationErrors,
  queryKey,
}: {
  isOpen: boolean;
  onClose: (isSubmit?: boolean) => void;
  onRecheckLocation?: () => void | Promise<void>;
  locationErrors?: CheckRequestError[];
  queryKey: string[];
}) {
  const { t } = useTranslation('contest');
  const theme = useMantineTheme();
  const troubleshooters =
    locationErrors?.flatMap((error) => error.troubleshooters ?? []).filter(Boolean) ?? [];
  const troubleshooterCounts = new Map<string, number>();
  const troubleshooterMessages = troubleshooters.map((message) => {
    const duplicateCount = troubleshooterCounts.get(message) ?? 0;
    troubleshooterCounts.set(message, duplicateCount + 1);

    return {
      key: duplicateCount ? `${message}-${duplicateCount}` : message,
      message,
    };
  });
  const hasTroubleshooters = troubleshooters.length > 0;

  // We have to specifically use useIsFetching to get the correct value in time, prop drilling doesn't work
  const isFetching = useIsFetching({ queryKey });
  const queryClient = useQueryClient();

  const handleRecheckLocationClick = async () => {
    await queryClient.invalidateQueries({ queryKey });
    await onRecheckLocation?.();
  };

  return (
    <StatusModalBody
      isOpen={isOpen}
      onClose={onClose}
      icon={<IconLocationBroken size={40} stroke={1.5} />}
      title={t('common.userStatusModals.invalidLocation.title')}
      text={
        <Trans
          i18nKey="contest:common.userStatusModals.invalidLocation.text"
          components={{
            link: (
              // @ts-ignore children are filled by the Trans component
              <ButtonLink
                mode="inlineLink"
                href="https://splashsports.com/where-can-you-play"
                target="_blank"
              />
            ),
          }}
        />
      }
      text2={
        hasTroubleshooters ? (
          <>
            <strong>{t('common.userStatusModals.invalidLocation.troubleshootingTitle')}</strong>
            {troubleshooterMessages.map(({ key, message }) => (
              <span
                key={key}
                style={{ display: 'block', marginTop: theme.other.spacing._16, whiteSpace: 'pre-line' }}
              >
                {message}
              </span>
            ))}
          </>
        ) : (
          <Trans
            i18nKey="contest:common.userStatusModals.common.contactSupport"
            components={{
              link: (
                // @ts-ignore children are filled by the Trans component
                <ButtonLink
                  mode="inlineLink"
                  href="mailto:support@splashsports.com?subject=Invalid Location Check"
                  target="_blank"
                />
              ),
            }}
          />
        )
      }
      text3={
        hasTroubleshooters ? (
          <Trans
            i18nKey="contest:common.userStatusModals.invalidLocation.troubleshootingContactSupport"
            components={{
              link: (
                // @ts-ignore children are filled by the Trans component
                <ButtonLink
                  mode="inlineLink"
                  href="mailto:support@splashsports.com?subject=Invalid Location Check"
                  target="_blank"
                />
              ),
            }}
          />
        ) : undefined
      }
      cta={
        <Button
          variant="secondary-fill"
          fullWidth
          onClick={handleRecheckLocationClick}
          loading={isFetching > 0}
        >
          {t('common.userStatusModals.common.recheckLocationCta')}
        </Button>
      }
    />
  );
}

export default InvalidLocationModal;
